import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { CompanyAddressPrompt } from '@/containers/sequences/company-address-prompt.component';
import { SenderAddedBanner } from '@/containers/sequences/sender-added-banner.component';
import { SequencesContainer } from '@/containers/sequences/sequences-container.component';
import { SequencingPage } from '@/containers/sequences/sequencing-page.component';
import { useSequencingNavigation } from '@/containers/sequences/use-sequencing-navigation.hook';

const detailSearchSchema = z.object({
  // An old link (?tab=settings) opens the default tab instead of erroring.
  tab: z
    .enum(['steps', 'people', 'launch', 'results', 'review'])
    .optional()
    .catch(undefined),
  // Review links from Slack, alerts and Tasks: open on this draft, under the
  // pill it is in. Anything else is ignored.
  draft: z.string().uuid().optional().catch(undefined),
  state: z.enum(['waiting', 'blocked']).optional().catch(undefined),
  // The "can send from your mailbox" alert: the mailbox a teammate added.
  sender_added: z.string().uuid().optional().catch(undefined),
});

export const Route = createFileRoute('/sequences/$sequenceId')({
  component: SequenceDetailRoute,
  validateSearch: detailSearchSchema,
});

function SequenceDetailRoute() {
  const { sequenceId } = Route.useParams();
  const { tab, draft, state, sender_added } = Route.useSearch();
  const navigate = useNavigate();
  const onNavigate = useSequencingNavigation();
  return (
    <SequencingPage title='Sequences'>
      <CompanyAddressPrompt />
      {sender_added && (
        <SenderAddedBanner
          sequenceId={sequenceId}
          mailboxId={sender_added}
          onDone={() =>
            void navigate({
              to: '/sequences/$sequenceId',
              params: { sequenceId },
              search: (prev) => ({ ...prev, sender_added: undefined }),
              replace: true,
            })
          }
        />
      )}
      <SequencesContainer
        selectedId={sequenceId}
        initialTab={tab}
        initialDraftId={tab === 'review' ? draft : undefined}
        initialDraftState={tab === 'review' ? state : undefined}
        onNavigate={onNavigate}
        onTabChange={(next) =>
          void navigate({
            to: '/sequences/$sequenceId',
            params: { sequenceId },
            search: next === 'steps' ? {} : { tab: next },
            replace: true,
          })
        }
        onSelectSequence={(id, tab) => {
          if (id)
            void navigate({
              to: '/sequences/$sequenceId',
              params: { sequenceId: id },
              search: tab ? { tab } : {},
            });
          else void navigate({ to: '/sequences' });
        }}
      />
    </SequencingPage>
  );
}
